// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Facet dropdown options for the Prospects list (and AI Prospects, which is the
 * SAME component with the origin pinned), extracted from the page so the option
 * lists can be unit tested without mounting the table.
 *
 * The keys here are HALF of a contract with `Api::V1::LeadsController`'s
 * `filterable(...)` declaration; the other half is pinned by
 * `components/table/config-parity.test.ts` via `PROSPECTS_TABLE.facetKeys`.
 *
 * `ai_generated` is deliberately NOT here. The controller declares it and the
 * server-key manifest lists it, but it is the ORIGIN the page itself pins
 * (regular vs. sales-agent prospects) — not something a viewer chooses — so it
 * never becomes a dropdown.
 */
import type { FacetDef } from "@crm-src/components/table/TableFilterBar";
import type { FacetKeysOf } from "@crm-src/components/table/configs";
import { PROSPECTS_TABLE } from "@crm-src/components/table/configs";
import { personLabel, type PersonLike } from "@crm-src/components/table/person-label";
import type { LeadFilters } from "@crm-src/types/lead";

/**
 * The Prospects facet keys, DERIVED from the shared config rather than
 * re-typed — so there is exactly one place a key can be misspelled, and
 * misspelling it there breaks the assertion below.
 */
export type ProspectFacetKey = FacetKeysOf<typeof PROSPECTS_TABLE>;

/**
 * Compile-time proof that every declared facet key is a param `LeadFilters`
 * actually carries — i.e. one the API implements. A key that only exists in the
 * config otherwise compiles fine and fails at RUNTIME as a param the server
 * drops, with `config-parity.test.ts` as the only net.
 */
type FacetKeysAreFilterFields =
  ProspectFacetKey extends keyof LeadFilters ? true : never;
const _facetKeysAreFilterFields: FacetKeysAreFilterFields = true;
void _facetKeysAreFilterFields;

interface ResellerLike { id: number | string; name: string }

/**
 * Priorities, mirroring `customers.priority`.
 *
 * FOUR values, not three: `Customer` validates `inclusion: { in: %w[low medium
 * high urgent] }` (`xop-brain/apps/crm-api/app/models/customer.rb`), and the
 * Prospects table's own priority chip renders `urgent` in red. Dropping it
 * would leave every urgent prospect unreachable from the filter bar; inventing
 * a value outside the set is not a loud error anywhere either — the dropdown
 * renders, the query goes out, and the server matches zero rows. Pinned by
 * `prospects-page-query.test.ts` and kept in step with `LEAD_PRIORITIES`
 * (`@crm-src/types/lead`), which the inline row editor uses.
 */
const PRIORITIES: FacetDef["options"] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

/**
 * Build the Prospects filter-bar facets.
 *
 * Options come from data the page ALREADY loads — the reseller list, the user
 * roster, the shared industry catalogue — plus the sources present in the
 * CURRENT result set, so no endpoint exists just to populate a dropdown.
 * "Direct" is the reseller-less case and maps to the server's
 * `reseller_id=none` sentinel; it leads the list because most prospects are
 * direct.
 *
 * Returns every declared facet, INCLUDING one whose options came back empty
 * (`useUsers()` answers 403 with `[]` for a non-admin, and a page of prospects
 * may carry no source at all): dropping an optionless facet from the rendered
 * controls is `TableFilterBar`'s job, so every list page inherits it. Hiding a
 * facet on PERMISSION — the page drops `reseller_id` for a viewer without
 * `crm:resellers` — is the caller's job. Either way this stays a pure function
 * of its inputs.
 */
export function buildProspectFacetDefs(
  resellers: ResellerLike[],
  users: PersonLike[],
  industries: readonly string[],
  sources: readonly string[],
): FacetDef<ProspectFacetKey>[] {
  return [
    {
      key: "reseller_id",
      label: "Reseller",
      options: [
        { value: "none", label: "Direct" },
        ...resellers.map((r) => ({ value: String(r.id), label: r.name })),
      ],
    },
    {
      key: "industry",
      label: "Industry",
      options: industries.map((i) => ({ value: i, label: i })),
    },
    {
      key: "salesperson_id",
      label: "Salesperson",
      options: users.map((u) => ({
        value: String(u.id),
        label: personLabel(u),
      })),
    },
    { key: "priority", label: "Priority", options: PRIORITIES },
    {
      // `prospect_source` is free text on the crm-api side (no enum to mirror
      // and no catalogue endpoint), so `sources` is the vocabulary of THIS
      // page's origin: `LEAD_SOURCES` — the same list the prospect form writes
      // from — for the regular page, `AI_PROSPECT_SOURCES` for AI Prospects,
      // whose rows carry the agent's marker instead. Deriving it from the
      // LOADED ROWS instead would be self-narrowing: pick "Referral" and every
      // other option vanishes with the rows that carried it. Blanks are
      // dropped (the server treats a blank facet as a no-op, so the option
      // would visibly do nothing) and the rest de-duplicated + sorted here
      // rather than at each call site.
      key: "prospect_source",
      label: "Source",
      options: [...new Set(sources.filter((s) => s && s.trim().length > 0))]
        .sort((a, b) => a.localeCompare(b))
        .map((s) => ({ value: s, label: s })),
    },
  ];
}
